import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Chat, Message, ChatParticipant, ChatSettings, MessageAttachment } from '../utils/types';

// مجموعات Firestore
const CHATS_COLLECTION = 'chats';
const MESSAGES_COLLECTION = 'messages';

// ===============================
// خدمات المحادثات (Chats)
// ===============================

/**
 * إنشاء محادثة جديدة
 */
export const createChat = async (chatData: Omit<Chat, 'id' | 'createdAt' | 'lastActivity'>): Promise<string> => {
  try {
    console.log('Creating chat with data:', chatData);

    // تنظيف البيانات من القيم undefined
    const cleanedData: any = {
      name: chatData.name || 'محادثة جديدة',
      type: chatData.type || 'group',
      participants: chatData.participants || [],
      createdBy: chatData.createdBy,
      isActive: chatData.isActive !== false,
      settings: chatData.settings || {
        allowFileSharing: true,
        allowReactions: true,
        allowReplies: true,
        maxFileSize: 10,
        allowedFileTypes: ['image/*', 'application/pdf', 'text/*'],
        muteNotifications: false,
        autoDeleteMessages: false,
        autoDeleteDays: 30
      },
      createdAt: serverTimestamp(),
      lastActivity: serverTimestamp(),
    };

    // إضافة الوصف فقط إذا كان موجوداً
    if (chatData.description && chatData.description.trim()) {
      cleanedData.description = chatData.description.trim();
    }

    // إضافة الصورة الرمزية فقط إذا كانت موجودة
    if (chatData.avatar) {
      cleanedData.avatar = chatData.avatar;
    }

    console.log('Cleaned data for Firebase:', cleanedData);

    const docRef = await addDoc(collection(db, CHATS_COLLECTION), cleanedData);
    console.log('Chat created successfully with ID:', docRef.id);

    return docRef.id;
  } catch (error) {
    console.error('Error creating chat:', error);
    console.error('Error details:', {
      code: (error as any)?.code,
      message: (error as any)?.message,
      stack: (error as any)?.stack
    });
    throw error;
  }
};

/**
 * الحصول على جميع المحادثات للمستخدم
 */
export const getUserChats = async (userId: string): Promise<Chat[]> => {
  try {
    // الحصول على جميع المحادثات أولاً
    const querySnapshot = await getDocs(collection(db, CHATS_COLLECTION));
    const chats: Chat[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const chat = {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastActivity: data.lastActivity?.toDate() || new Date(),
      } as Chat;

      // التحقق من أن المستخدم مشارك في المحادثة
      const isParticipant = chat.participants.some(p => p.userId === userId);
      if (isParticipant) {
        chats.push(chat);
      }
    });

    // ترتيب حسب آخر نشاط
    return chats.sort((a, b) => b.lastActivity.getTime() - a.lastActivity.getTime());
  } catch (error) {
    console.error('Error getting user chats:', error);
    throw error;
  }
};

/**
 * الحصول على محادثة بالمعرف
 */
export const getChatById = async (chatId: string): Promise<Chat | null> => {
  try {
    const docRef = doc(db, CHATS_COLLECTION, chatId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastActivity: data.lastActivity?.toDate() || new Date(),
      } as Chat;
    }

    return null;
  } catch (error) {
    console.error('Error getting chat:', error);
    throw error;
  }
};

/**
 * تحديث محادثة
 */
export const updateChat = async (chatId: string, updates: Partial<Chat>): Promise<void> => {
  try {
    const docRef = doc(db, CHATS_COLLECTION, chatId);
    await updateDoc(docRef, {
      ...updates,
      lastActivity: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating chat:', error);
    throw error;
  }
};

/**
 * حذف محادثة
 */
export const deleteChat = async (chatId: string): Promise<void> => {
  try {
    const docRef = doc(db, CHATS_COLLECTION, chatId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting chat:', error);
    throw error;
  }
};

/**
 * إضافة مشارك إلى المحادثة
 */
export const addParticipantToChat = async (chatId: string, participant: ChatParticipant): Promise<void> => {
  try {
    const docRef = doc(db, CHATS_COLLECTION, chatId);
    await updateDoc(docRef, {
      participants: arrayUnion(participant),
      lastActivity: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error adding participant:', error);
    throw error;
  }
};

/**
 * إزالة مشارك من المحادثة
 */
export const removeParticipantFromChat = async (chatId: string, userId: string): Promise<void> => {
  try {
    const chat = await getChatById(chatId);
    if (!chat) throw new Error('Chat not found');

    const updatedParticipants = chat.participants.filter(p => p.userId !== userId);
    
    const docRef = doc(db, CHATS_COLLECTION, chatId);
    await updateDoc(docRef, {
      participants: updatedParticipants,
      lastActivity: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error removing participant:', error);
    throw error;
  }
};

/**
 * البحث عن محادثة خاصة بين مستخدمين
 */
export const findPrivateChat = async (userId1: string, userId2: string): Promise<Chat | null> => {
  try {
    const q = query(
      collection(db, CHATS_COLLECTION),
      where('type', '==', 'private')
    );

    const querySnapshot = await getDocs(q);

    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      const chat = {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastActivity: data.lastActivity?.toDate() || new Date(),
      } as Chat;

      // التحقق من وجود كلا المستخدمين
      const hasUser1 = chat.participants.some(p => p.userId === userId1);
      const hasUser2 = chat.participants.some(p => p.userId === userId2);

      if (hasUser1 && hasUser2 && chat.participants.length === 2) {
        return chat;
      }
    }

    return null;
  } catch (error) {
    console.error('Error finding private chat:', error);
    throw error;
  }
};

/**
 * الاستماع للتغييرات في المحادثات
 */
export const subscribeToUserChats = (
  userId: string,
  callback: (chats: Chat[]) => void
): (() => void) => {
  return onSnapshot(collection(db, CHATS_COLLECTION), (querySnapshot) => {
    const chats: Chat[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const chat = {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastActivity: data.lastActivity?.toDate() || new Date(),
      } as Chat;

      // التحقق من أن المستخدم مشارك في المحادثة
      const isParticipant = chat.participants.some(p => p.userId === userId);
      if (isParticipant) {
        chats.push(chat);
      }
    });

    // ترتيب حسب آخر نشاط
    const sortedChats = chats.sort((a, b) => b.lastActivity.getTime() - a.lastActivity.getTime());
    callback(sortedChats);
  });
};

// ===============================
// خدمات الرسائل (Messages)
// ===============================

/**
 * إرسال رسالة جديدة
 */
export const sendMessage = async (messageData: Omit<Message, 'id' | 'timestamp'>): Promise<string> => {
  try {
    const newMessage = {
      ...messageData,
      timestamp: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, MESSAGES_COLLECTION), newMessage);

    // تحديث آخر رسالة في المحادثة
    await updateChat(messageData.chatId, {
      lastMessage: { ...newMessage, id: docRef.id, timestamp: new Date() } as Message,
    });

    return docRef.id;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * الحصول على رسائل المحادثة
 */
export const getChatMessages = async (chatId: string, limitCount: number = 50): Promise<Message[]> => {
  try {
    const q = query(
      collection(db, MESSAGES_COLLECTION),
      where('chatId', '==', chatId),
      where('isDeleted', '==', false),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const messages: Message[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      messages.push({
        id: doc.id,
        ...data,
        timestamp: data.timestamp?.toDate() || new Date(),
        editedAt: data.editedAt?.toDate(),
        deletedAt: data.deletedAt?.toDate(),
      } as Message);
    });

    return messages.reverse(); // ترتيب تصاعدي للعرض
  } catch (error) {
    console.error('Error getting chat messages:', error);
    throw error;
  }
};

/**
 * تحديث رسالة
 */
export const updateMessage = async (messageId: string, updates: Partial<Message>): Promise<void> => {
  try {
    const docRef = doc(db, MESSAGES_COLLECTION, messageId);
    await updateDoc(docRef, {
      ...updates,
      isEdited: true,
      editedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating message:', error);
    throw error;
  }
};

/**
 * حذف رسالة (حذف منطقي)
 */
export const deleteMessage = async (messageId: string): Promise<void> => {
  try {
    const docRef = doc(db, MESSAGES_COLLECTION, messageId);
    await updateDoc(docRef, {
      isDeleted: true,
      deletedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

/**
 * تمييز الرسالة كمقروءة
 */
export const markMessageAsRead = async (messageId: string, userId: string): Promise<void> => {
  try {
    const docRef = doc(db, MESSAGES_COLLECTION, messageId);
    await updateDoc(docRef, {
      readBy: arrayUnion(userId),
    });
  } catch (error) {
    console.error('Error marking message as read:', error);
    throw error;
  }
};

/**
 * الاستماع للرسائل الجديدة في المحادثة
 */
export const subscribeToMessages = (
  chatId: string,
  callback: (messages: Message[]) => void
): (() => void) => {
  const q = query(
    collection(db, MESSAGES_COLLECTION),
    where('chatId', '==', chatId),
    where('isDeleted', '==', false),
    orderBy('timestamp', 'asc')
  );

  return onSnapshot(q, (querySnapshot) => {
    const messages: Message[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      messages.push({
        id: doc.id,
        ...data,
        timestamp: data.timestamp?.toDate() || new Date(),
        editedAt: data.editedAt?.toDate(),
        deletedAt: data.deletedAt?.toDate(),
      } as Message);
    });
    callback(messages);
  });
};
